import { useCallback, useMemo, useState } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import { countActiveFilters } from '@/utils/filters';
import { LIST_PAGE_SIZE } from '../constants/instituteSubmission.constants';
import type { InstituteSubmissionFilters } from '../types/filters.types';
import type { InstituteSubmissionListParams } from '../types/instituteSubmission.types';
import {
  clearDependentFilters,
  toCountsBySubmitterParams,
  toFilterValues,
  toSearchFilters,
  toSummaryParams,
} from '../utils/instituteSubmissionFilters';

const instituteSubmissionsRouteApi = getRouteApi('/_auth/settings/institute-submissions/');

export function useInstituteSubmissionFilters() {
  const search = instituteSubmissionsRouteApi.useSearch();
  const navigate = instituteSubmissionsRouteApi.useNavigate();
  const [filterOpen, setFilterOpen] = useState(false);

  const appliedValues = useMemo(() => toFilterValues(search), [search]);
  const summaryParams = useMemo(() => toSummaryParams(search), [search]);

  const listParams = useMemo<InstituteSubmissionListParams>(
    () => ({ ...summaryParams, status: search.reviewStatus, pageSize: LIST_PAGE_SIZE }),
    [summaryParams, search.reviewStatus],
  );
  const countsBySubmitterParams = useMemo(() => toCountsBySubmitterParams(search), [search]);

  const { draftValues, setDraftValues, applyValues, hasPendingChanges } = useFilterDraft({
    appliedValues,
    shouldWaitForApply: true,
    onApply: (filters) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toSearchFilters(filters) }),
        replace: true,
        resetScroll: false,
      });
    },
  });

  const appliedSearch = search.search;

  const handleSearchApply = useCallback(
    (searchText: string) => {
      const nextSearch = searchText.trim() || undefined;
      if (nextSearch === appliedSearch) return;
      void navigate({
        search: (previous) => ({ ...previous, search: nextSearch }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate, appliedSearch],
  );

  function handleFilterChange(nextFilters: InstituteSubmissionFilters) {
    setDraftValues((previous) => clearDependentFilters(previous, nextFilters));
  }

  function applyFilterValues(nextFilters: InstituteSubmissionFilters) {
    applyValues(clearDependentFilters(draftValues, nextFilters));
  }

  const activeFilterCount = countActiveFilters(appliedValues);

  return {
    searchText: search.search ?? '',
    filterValues: draftValues,
    filterOpen,
    setFilterOpen,
    activeFilterCount,
    hasPendingChanges,
    listParams,
    summaryParams,
    countsBySubmitterParams,
    handleSearchApply,
    handleFilterChange,
    applyFilterValues,
  };
}
